// apps/merchant-v3/src/services/supportClient.ts
//
// B8 — thin wrapper over apiClient for the merchant Support (tickets) module.
// Talks to the upgraded support-ticket backend at /api/v1/tickets. Merchant
// tokens are auto-scoped server-side to the caller's own tickets.

import { apiClient } from '@/lib/api/client';
import type { ComposeAttachment } from '@printway/ui';

export type TicketStatus =
  | 'Open'
  | 'InProgress'
  | 'AwaitingHandler'
  | 'PendingSupportReview'
  | 'Resolved'
  | 'Closed'
  | 'Reopened';

/** Nhãn hiển thị phía merchant — 2 trạng thái dispatch nội bộ gom về InProgress (§5). */
export type TicketDisplayStatus = 'Open' | 'InProgress' | 'Resolved' | 'Closed' | 'Reopened';

export function displayStatus(s: TicketStatus): TicketDisplayStatus {
  return s === 'AwaitingHandler' || s === 'PendingSupportReview' ? 'InProgress' : s;
}
export type TicketPriority = 'Critical' | 'High' | 'Medium' | 'Low';
export type TicketCategory = 'Order' | 'Billing' | 'Product' | 'Account';

export const TICKET_CATEGORIES: TicketCategory[] = ['Order', 'Billing', 'Product', 'Account'];
// Merchant-created tickets are capped at High server-side.
export const MERCHANT_PRIORITIES: TicketPriority[] = ['Low', 'Medium', 'High'];

/** Structured satisfaction survey — each sub-score 1..5. */
export interface RatingSurvey {
  responsiveness: number;
  helpfulness: number;
  professionalism: number;
}

export interface TicketUserSummary {
  id: string;
  name: string;
  email: string;
}

export type TicketResolutionType =
  | 'Reprint'
  | 'FullRefund'
  | 'PartialRefund'
  | 'StoreCredit'
  | 'Replacement'
  | 'ShippingCredit'
  | 'NoActionRequired'
  | 'Escalated';

export interface TicketRow {
  _id: string;
  /** Human-facing code TK<yyyymmdd><seq> — null on legacy rows. */
  ticketNumber: string | null;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  assigneeId: string | null;
  assignee: TicketUserSummary | null;
  escalationLevel: number;
  firstResponseSla: number | null;
  resolutionSla: number | null;
  firstRespondedAt: number | null;
  resolvedAt: number | null;
  createdAt: number;
  updatedAt: number;
  /** How the ticket was closed out (visible to the merchant post-resolve). */
  resolutionType: TicketResolutionType | null;
  resolutionNotes: string;
  /** Merchant satisfaction rating 1..5 — null until the merchant rates. */
  rating: number | null;
  ratingComment: string;
  /** Optional structured survey (3 sub-scores 1..5); null when skipped. */
  ratingSurvey?: RatingSurvey | null;
  /** Denormalized latest-message time (unread computation). */
  lastMessageAt?: number | null;
  /** Short preview of the latest message (list card, optional). */
  lastMessagePreview?: string;
  /** Per-caller unread flag (dbg-20260705-ticket-realtime-notify). */
  unread?: boolean;
  /** Linked order chips resolved by the list endpoint (dbg-ticket-list-clone). */
  relatedOrders?: Array<{ id: string; orderCode: string; status?: string }>;
  /** Thread reply count for the list card (dbg-ticket-list-clone). */
  messageCount?: number;
}

export interface TicketMessage {
  _id: string;
  ticketId: string;
  authorId: string;
  type: 'public' | 'internal';
  content: string;
  attachments: unknown[];
  createdAt: number;
}

export interface TicketDetail extends TicketRow {
  messages: TicketMessage[];
  /**
   * Read-receipt cursor (ms): newest time the OTHER side read this ticket.
   * A sender's own message shows a green double-check once its createdAt is
   * ≤ this value. 0/undefined = nobody else has opened it yet.
   */
  counterpartyReadAt?: number | null;
}

export interface ListTicketsParams {
  status?: TicketStatus;
  page?: number;
  pageSize?: number;
}

export interface ListTicketsResult {
  rows: TicketRow[];
  page: number;
  pageSize: number;
  total: number;
}

export async function listTickets(params: ListTicketsParams = {}): Promise<ListTicketsResult> {
  const q = new URLSearchParams();
  if (params.status) q.set('status', params.status);
  q.set('page', String(params.page ?? 1));
  q.set('pageSize', String(params.pageSize ?? 20));
  return apiClient.get<ListTicketsResult>(`/api/v1/tickets?${q.toString()}`);
}

/**
 * The caller's own unread ticket count, computed server-side.
 *
 * The sidebar badge used to derive this by pulling 100 ticket rows and
 * filtering client-side, on a 15s poll — ~14KB a request for one integer.
 */
export async function getTicketUnreadCount(): Promise<{ unread: number }> {
  return apiClient.get<{ unread: number }>('/api/v1/tickets/unread-count');
}

export async function getTicket(id: string): Promise<TicketDetail> {
  // BE wraps detail as `{ ticket, messages }` (ticket.handler.ts). Normalise
  // to the flat TicketDetail shape so page code reads `detail.subject` etc.
  const raw = await apiClient.get<
    TicketDetail | { ticket: TicketRow; messages: TicketMessage[] }
  >(`/api/v1/tickets/${id}`);
  if (raw && typeof raw === 'object' && 'ticket' in raw) {
    return { ...(raw.ticket as TicketRow), messages: raw.messages ?? [] };
  }
  return raw as TicketDetail;
}

/** POST /:id/rate — merchant satisfaction rating (1..5, once, post-Resolve). */
export async function rateTicket(
  id: string,
  rating: number,
  comment?: string,
  survey?: RatingSurvey | null,
): Promise<TicketRow> {
  return apiClient.post<TicketRow>(`/api/v1/tickets/${id}/rate`, {
    rating,
    ...(comment ? { comment } : {}),
    ...(survey ? { survey } : {}),
  });
}

export interface CreateTicketBody {
  subject: string;
  description: string;
  category: TicketCategory;
  priority?: TicketPriority;
  /** Single order link (legacy merchant modal). */
  orderId?: string;
  /** Multiple linked orders — server caps at 20, merges with `orderId`. */
  relatedOrderIds?: string[];
  /** Linked invoice (P4.1.D-17 / G-17 "Dispute this invoice"). Marks the
   *  ticket as an invoice_dispute; persisted server-side as `invoiceId`. */
  invoiceId?: string;
}

export async function createTicket(body: CreateTicketBody): Promise<TicketRow> {
  return apiClient.post<TicketRow>('/api/v1/tickets', body);
}

/** Mark a ticket read for the current merchant (dbg-20260705). Best-effort. */
export async function markTicketRead(id: string): Promise<void> {
  try {
    await apiClient.post<{ ok: boolean }>(`/api/v1/tickets/${id}/read`, {});
  } catch {
    /* unread cursor is non-critical UI state */
  }
}

export interface PostMessageInput {
  content: string;
  attachments?: ComposeAttachment[];
  relatedOrderIds?: string[];
}

export async function postTicketMessage(id: string, input: PostMessageInput): Promise<TicketMessage> {
  return apiClient.post<TicketMessage>(`/api/v1/tickets/${id}/messages`, {
    content: input.content,
    attachments: input.attachments ?? [],
    relatedOrderIds: input.relatedOrderIds ?? [],
  });
}

/**
 * PUT /:id/related-orders — set the ticket's linked orders at TICKET level
 * (sidebar card). Replaces the full set; server best-effort filters to the
 * ticket merchant's own orders. Normalises the `{ ticket, messages }` envelope
 * to the flat TicketDetail so callers read `.relatedOrders` directly.
 */
export async function setTicketRelatedOrders(
  id: string,
  orderIds: string[],
): Promise<TicketDetail> {
  const raw = await apiClient.put<
    TicketDetail | { ticket: TicketRow; messages?: TicketMessage[] }
  >(`/api/v1/tickets/${id}/related-orders`, { orderIds });
  if (raw && typeof raw === 'object' && 'ticket' in raw) {
    return { ...(raw.ticket as TicketRow), messages: raw.messages ?? [] };
  }
  return raw as TicketDetail;
}

/** POST /:id/reopen — merchant chưa hài lòng: Resolved → Reopened (Plan 5). */
export async function reopenTicket(id: string, comment?: string): Promise<TicketRow> {
  const trimmed = comment?.trim() ?? '';
  return apiClient.post<TicketRow>(
    `/api/v1/tickets/${id}/reopen`,
    trimmed ? { comment: trimmed } : {},
  );
}

/** POST /:id/confirm-close — merchant xác nhận: Resolved → Closed (Plan 5). */
export async function confirmCloseTicket(id: string): Promise<TicketRow> {
  return apiClient.post<TicketRow>(`/api/v1/tickets/${id}/confirm-close`, {});
}

/** Upload one file → GridFS; returns a typed file attachment for the compose box. */
export async function uploadTicketAttachment(
  id: string,
  file: File,
): Promise<Extract<ComposeAttachment, { kind: 'file' }>> {
  const form = new FormData();
  form.append('file', file, file.name);
  // Do NOT set Content-Type manually — the browser adds the multipart boundary.
  const data = await apiClient.post<{
    fileId: string;
    filename: string;
    contentType: string;
    size: number;
  }>(`/api/v1/tickets/${id}/attachments`, form);
  return {
    kind: 'file',
    fileId: data.fileId,
    filename: data.filename,
    contentType: data.contentType,
    size: data.size,
  };
}
